import React from 'react';
import { Avatar, Badge, Button } from '@opendate-app/setlist';
import {
  COMPETITOR_COLOR, RELATIONSHIPS, YOURS_COLOR, formatDate, initials, matchesSelection,
} from './competitiveUtils';

const GAP_LIMIT = 6;
const RECENT_LIMIT = 5;
const caption = { fontSize: 12, lineHeight: '18px', color: 'var(--sl-text-quaternary)' };
const listRow = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
  background: 'var(--sl-bg-primary)', border: '1px solid var(--sl-border-secondary)', width: '100%', textAlign: 'left',
};

function BiggestGaps({ agents, yourName, competitorName, onPickAgent }) {
  const gaps = agents.filter((row) => row.yours === 0 && row.competitor > 0)
    .sort((a, b) => b.competitor - a.competitor)
    .slice(0, GAP_LIMIT);
  return (
    <>
      <div>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--sl-text-primary)' }}>Biggest gaps</div>
        <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>{`Agents booking ${competitorName} who haven't booked ${yourName} in this range.`}</div>
      </div>
      <div className="d-flex flex-column" style={{ gap: 4 }}>
        {gaps.map((gap) => (
          <button type="button" key={gap.key} style={{ ...listRow, gap: 12, padding: '8px 10px' }} onClick={() => onPickAgent(gap.key)}>
            <Avatar size="sm" initials={initials(gap.name)} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="text-truncate" style={{ fontSize: 14, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{gap.name}</div>
              <div className="text-truncate" style={{ fontSize: 12, color: 'var(--sl-text-tertiary)' }}>{gap.agency || 'Unknown agency'}</div>
            </div>
            <div className="text-right">
              <div style={{ fontSize: 14, fontWeight: 600, color: COMPETITOR_COLOR }}>{gap.competitor}</div>
              <div style={{ fontSize: 10, color: 'var(--sl-text-quaternary)' }}>shows</div>
            </div>
          </button>
        ))}
        {gaps.length === 0 && <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>No gaps in this range.</div>}
      </div>
    </>
  );
}

function SelectionDetail({
  row, level, agents, events, yourName, competitorName, onPickAgent, onClear,
}) {
  const total = row.yours + row.competitor || 1;
  const recent = events
    .filter((event) => event.venue === 'competitor' && matchesSelection(event, { level, key: row.key }))
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, RECENT_LIMIT);
  const agencyAgents = level === 'agency'
    ? agents.filter((agent) => agent.agency === row.key).sort((a, b) => b.competitor - a.competitor)
    : [];

  return (
    <>
      <div className="d-flex justify-content-between align-items-start" style={{ gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ ...caption, textTransform: 'uppercase', letterSpacing: '.04em' }}>{level === 'agency' ? 'Agency' : 'Agent'}</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--sl-text-primary)' }}>{row.name}</div>
          <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>
            {level === 'agency' ? `${row.agent_count} ${row.agent_count === 1 ? 'agent' : 'agents'}` : (row.agency || 'Unknown agency')}
          </div>
        </div>
        <Button variant="tertiary" size="sm" onClick={onClear} aria-label="Clear selection">✕</Button>
      </div>
      <span><Badge size="sm" color={RELATIONSHIPS[row.relationship].color}>{RELATIONSHIPS[row.relationship].label}</Badge></span>
      <div className="d-flex flex-column" style={{ gap: 8 }}>
        <div className="d-flex justify-content-between" style={{ fontSize: 14, color: 'var(--sl-text-secondary)' }}>
          <span><b style={{ color: YOURS_COLOR }}>{row.yours}</b>{` at ${yourName}`}</span>
          <span><b style={{ color: COMPETITOR_COLOR }}>{row.competitor}</b>{` at ${competitorName}`}</span>
        </div>
        <div className="d-flex" style={{ height: 8, borderRadius: 9999, overflow: 'hidden', background: 'var(--sl-bg-tertiary)' }}>
          <div style={{ width: `${(row.yours / total) * 100}%`, background: YOURS_COLOR }} />
          <div style={{ width: `${(row.competitor / total) * 100}%`, background: COMPETITOR_COLOR }} />
        </div>
        <div style={caption}>{`Last at ${yourName}: ${row.last_yours ? formatDate(row.last_yours) : 'never in range'}`}</div>
      </div>
      {agencyAgents.length > 0 && (
        <div className="d-flex flex-column" style={{ gap: 6 }}>
          <div style={caption}>Agents</div>
          {agencyAgents.map((agent) => (
            <button type="button" key={agent.key} style={listRow} onClick={() => onPickAgent(agent.key)}>
              <span className="text-truncate" style={{ flex: 1, fontSize: 14, color: 'var(--sl-text-primary)' }}>{agent.name}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: YOURS_COLOR }}>{agent.yours}</span>
              <span style={{ fontSize: 12, color: 'var(--sl-text-quinary)' }}>/</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: COMPETITOR_COLOR }}>{agent.competitor}</span>
            </button>
          ))}
        </div>
      )}
      <div className="d-flex flex-column" style={{ gap: 6 }}>
        <div style={caption}>{`Recent at ${competitorName}`}</div>
        {recent.map((event) => (
          <div key={event.id} className="d-flex align-items-baseline" style={{ gap: 10, fontSize: 14 }}>
            <span style={{ width: 52, flex: 'none', ...caption }}>{formatDate(event.date, { month: 'short', day: 'numeric' })}</span>
            <span className="text-truncate" style={{ color: 'var(--sl-text-primary)' }}>{event.artist}</span>
          </div>
        ))}
        {recent.length === 0 && <div style={{ fontSize: 14, color: 'var(--sl-text-tertiary)' }}>None in this range.</div>}
      </div>
    </>
  );
}

function CompetitiveSidePanel({ selectedRow, ...props }) {
  return (
    <aside
      className="d-flex flex-column"
      style={{
        flex: '1 1 300px', minWidth: 0, gap: 16, padding: 20,
        borderLeft: '1px solid var(--sl-border-secondary)', borderTop: '1px solid var(--sl-border-secondary)',
        marginTop: -1, marginLeft: -1, background: 'var(--sl-bg-secondary)',
      }}
    >
      {selectedRow ? <SelectionDetail row={selectedRow} {...props} /> : <BiggestGaps {...props} />}
    </aside>
  );
}

export default CompetitiveSidePanel;
